import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export interface InvoiceForPdf {
  propertyName?: string;
  propertyAddress?: string;
  tenantName?: string;
  unitNumber?: string;
  period?: string;
  totalAmountCents: number;
  lineItems: Array<{ label: string; amountCents: number }>;
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatPeriod(period: string | undefined): string {
  if (!period) return '';
  const [year, month] = period.split('-');
  if (!year || !month) return period;
  const date = new Date(parseInt(year), parseInt(month) - 1, 1);
  if (isNaN(date.getTime())) return period;
  return date.toLocaleString('default', { month: 'long', year: 'numeric' });
}

export function generateInvoicePdf(invoice: InvoiceForPdf): void {
  const doc = new jsPDF();

  // Header
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Utilibill', 14, 20);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(invoice.propertyName ?? '—', 14, 28);
  doc.text(invoice.propertyAddress ?? '—', 14, 34);

  // Invoice metadata
  doc.setFontSize(10);
  const metaY = 46;
  if (invoice.period) {
    doc.text(`Period: ${formatPeriod(invoice.period)}`, 14, metaY);
  }
  if (invoice.unitNumber) {
    doc.text(`Unit: ${invoice.unitNumber}`, 14, metaY + 6);
  }
  if (invoice.tenantName) {
    doc.text(`Tenant: ${invoice.tenantName}`, 14, metaY + 12);
  }

  // Line items table
  const tableRows = invoice.lineItems.map((li) => [li.label, formatCents(li.amountCents)]);

  autoTable(doc, {
    startY: metaY + 22,
    head: [['Description', 'Amount']],
    body: tableRows,
    foot: [['Total Due', formatCents(invoice.totalAmountCents)]],
    footStyles: { fontStyle: 'bold' },
    headStyles: { fillColor: [25, 118, 210] },
    columnStyles: { 1: { halign: 'right' } },
    margin: { left: 14, right: 14 },
  });

  // Footer
  const pageH = doc.internal.pageSize.height;
  doc.setFontSize(8);
  doc.setTextColor(150);
  doc.text('Generated by Utilibill', 14, pageH - 10);

  const periodSlug = invoice.period ?? 'invoice';
  const unitSlug = invoice.unitNumber ? `-unit-${invoice.unitNumber}` : '';
  doc.save(`invoice-${periodSlug}${unitSlug}.pdf`);
}
